import { useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperclip, faTrash } from "@fortawesome/free-solid-svg-icons";

import Button from "components/Button";
import HollowButton from "components/HollowButton";

import councilPortalService from "api/councilPortalService";
import { ICouncilSession } from "dto/Council/ICouncil";
import { ENotificationType, notification } from "utils/notification";

import { uploadPublicFile } from "./uploadPublicFile";
import styles from "./styles.module.scss";

interface Props {
  session: ICouncilSession;
  onClose: () => void;
  onSaved: (session: ICouncilSession) => void;
}

// Protokół sesji jako gotowy plik.
//
// Magistrat generował protokół z zapisu przebiegu obrad (AgendaRepository.GenerateProtocol);
// tej części nie przenieśliśmy, więc protokół powstaje poza systemem, a tutaj tylko
// wskazujemy plik i decydujemy, czy jest jawny.
//
// Plik NIE jest szyfrowany — trafia na publiczny portal, tak samo jak załączniki
// porządku obrad.

const errorText = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { Message?: string } } })?.response?.data?.Message ??
  fallback;

const ProtocolModal = ({ session, onClose, onSaved }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);

  // fileUri wypełnia się dopiero po wgraniu nowego pliku — Uri już podpiętego dokumentu
  // nie wychodzi z serwera, więc „zostaw jak jest" wyrażamy brakiem wartości, a odpięcie
  // osobną flagą.
  const [fileUri, setFileUri] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(session.ProtocolFileName);
  const [removeFile, setRemoveFile] = useState(false);
  const [published, setPublished] = useState(session.IsProtocolPublished);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { mutate: save, isLoading: saving } = councilPortalService.useSaveProtocol();

  const pick = async (file: File | null | undefined) => {
    if (!file) return;

    setUploading(true);
    setError(null);

    try {
      const uploaded = await uploadPublicFile(file);

      setFileUri(uploaded.Uri);
      setFileName(uploaded.FileName);
      setRemoveFile(false);
    } catch (e) {
      setError(errorText(e, "Nie udało się wysłać pliku."));
    } finally {
      setUploading(false);
    }
  };

  const submit = () => {
    save(
      {
        sessionId: session.Id,
        request: {
          FileUri: fileUri,
          FileName: fileName,
          RemoveFile: removeFile,
          IsPublished: published,
        },
      },
      {
        onSuccess: (result) => onSaved(result.data),
        onError: (e) =>
          notification({
            type: ENotificationType.ERROR,
            title: "Błąd",
            text: errorText(e, "Nie udało się zapisać protokołu."),
          }),
      },
    );
  };

  const hasFile = !!fileUri || (!removeFile && session.HasProtocol);

  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div className={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
        <div className={styles.modalTitle}>Protokół z sesji {session.Number}</div>

        <div className={styles.modalBody}>
          <input
            ref={inputRef}
            type="file"
            accept=".pdf"
            style={{ display: "none" }}
            onChange={(e) => {
              pick(e.target.files?.[0]);
              e.target.value = "";
            }}
          />

          {hasFile ? (
            <div className={styles.attachmentRow}>
              <FontAwesomeIcon icon={faPaperclip} />
              <div className={styles.attachmentInfo}>
                <span className={styles.attachmentTitle}>{fileName}</span>
                <span className={styles.attachmentFile}>
                  {fileUri ? "plik wgrany — zapisz, aby podpiąć" : "plik podpięty"}
                </span>
              </div>
              <button
                type="button"
                className={`${styles.toolBtn} ${styles.toolBtnDanger}`}
                title="Odepnij plik"
                onClick={() => {
                  setFileUri(null);
                  setFileName(null);
                  setRemoveFile(true);
                  setPublished(false);
                }}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          ) : (
            <div className={styles.emptyAttachments}>
              Nie podpięto pliku protokołu.
            </div>
          )}

          <Button
            text={uploading ? "Wysyłanie…" : "Wybierz plik PDF"}
            element={<FontAwesomeIcon icon={faPaperclip} />}
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
          />

          <label className={styles.radioOption}>
            <input
              type="checkbox"
              checked={published}
              disabled={!hasFile}
              onChange={(e) => setPublished(e.target.checked)}
            />
            Opublikuj protokół w portalu radnego
          </label>

          <span className={styles.fieldLabel}>
            Opublikowany protokół jest dostępny publicznie, bez logowania — tak samo jak
            załączniki porządku obrad.
          </span>

          {!!error && <span className={styles.fieldError}>{error}</span>}
        </div>

        <div className={styles.modalFooter}>
          <HollowButton text="Anuluj" onClick={onClose} />
          <Button
            text="Zapisz"
            loading={saving}
            disabled={uploading}
            onClick={submit}
          />
        </div>
      </div>
    </div>
  );
};

export default ProtocolModal;
